// ── Chart theme: one Dark / Light switch for every analytics chart ─────────
//
// The choice is app-wide, not per chart: flip it on one page and every chart
// on every page follows, including any already on screen (they listen for the
// change). It is a per-viewer convenience, so it lives in localStorage and a
// blocked or empty store simply means Light.
//
// Light is ACE's normal palette. Dark is the presentation look — a navy card
// with bright marks — built for showing the book to someone, not for working.
//
// Colours are plain strings so they can go straight onto SVG attributes. The
// light set uses the brand tokens, which carry literal fallbacks; a var()
// without one would paint SVG fills black.

import { useState, useEffect, useRef, useCallback } from "react";
import { B, INK_MUTED, INK_PRIMARY, SURFACE, SURFACE_MUTED,
         HAIRLINE, HAIRLINE_FIRM } from "./theme";

const KEY    = "ace.chart.theme";
const LEGACY = "ace.portfolioBalance.chart";     // the first page to have a switch
const EVT    = "ace-chart-theme";

const read = () => {
  try {
    const v = localStorage.getItem(KEY) || localStorage.getItem(LEGACY);
    return v === "dark" || v === "showcase" ? "dark" : "light";
  } catch { return "light"; }
};

export const CHART_THEMES = {
  light: {
    mode:"light", dark:false,
    ink:INK_PRIMARY, muted:INK_MUTED, grid:HAIRLINE, gridFirm:HAIRLINE_FIRM,
    surface:SURFACE, panel:SURFACE_MUTED, title:B.cyanDark, titleRule:B.cyanLight,
    accent:B.cyanDark, accentSoft:B.cyan, accentTop:B.cyan, accentBot:B.cyanDark,
    warm:B.amberDark, warmSoft:B.amber, warmTop:B.amberMid, warmBot:B.amber,
    tipBg:SURFACE, tipBorder:HAIRLINE_FIRM, tipShadow:"0 2px 8px rgba(0,0,0,0.10)",
  },
  dark: {
    mode:"dark", dark:true,
    ink:"#ffffff", muted:"rgba(255,255,255,0.62)",
    grid:"rgba(255,255,255,0.09)", gridFirm:"rgba(255,255,255,0.28)",
    surface:"#0b1d2a", panel:"rgba(255,255,255,0.07)", title:"#ffffff",
    titleRule:"rgba(255,255,255,0.14)",
    accent:"#38d6f5", accentSoft:"#5ee7ff", accentTop:"#5ee7ff", accentBot:"#0f8fb3",
    warm:"#ffc073", warmSoft:"#ffd479", warmTop:"#ffc073", warmBot:"#9a5b17",
    tipBg:"#10293a", tipBorder:"rgba(255,255,255,0.18)", tipShadow:"0 4px 14px rgba(0,0,0,0.45)",
  },
};

// The navy card behind a dark chart. Spread over a Card's own style.
export const DARK_CARD = {
  border:"none",
  background:"radial-gradient(120% 140% at 15% 0%, #12344a 0%, #0b1d2a 55%, #07141d 100%)",
  boxShadow:"0 8px 28px rgba(7,20,29,0.35)",
};
export const chartCardStyle = T => (T && T.dark ? DARK_CARD : null);

// Returns the active theme's tokens plus .set(mode).
export const useChartTheme = () => {
  const [mode, setMode] = useState(read);
  useEffect(() => {
    const h = e => setMode(e.detail === "dark" ? "dark" : "light");
    window.addEventListener(EVT, h);
    return () => window.removeEventListener(EVT, h);
  }, []);
  const set = useCallback(v => {
    try { localStorage.setItem(KEY, v); } catch { /* per-viewer nicety only */ }
    window.dispatchEvent(new CustomEvent(EVT, { detail: v }));
  }, []);
  return { ...CHART_THEMES[mode], set };
};

// The Dark | Light pill. Drop it in any chart card's header.
export const ChartThemeToggle = () => {
  const T = useChartTheme();
  return (
    <div style={{ display:"inline-flex", gap:2, padding:2, borderRadius:8, flexShrink:0,
                  background: T.dark ? "rgba(255,255,255,0.08)" : SURFACE_MUTED }}>
      {[["dark","Dark"],["light","Light"]].map(([k, l]) => {
        const on = T.mode === k;
        return (
          <button key={k} onClick={() => T.set(k)}
            style={{ border:"none", cursor:"pointer", fontSize:11, fontWeight:600,
                     padding:"4px 12px", borderRadius:6,
                     background: on ? (T.dark ? "#38d6f5" : SURFACE) : "transparent",
                     boxShadow: on && !T.dark ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                     color: on ? (T.dark ? "#062030" : INK_PRIMARY)
                               : (T.dark ? "rgba(255,255,255,0.65)" : INK_MUTED) }}>{l}</button>
        );
      })}
    </div>
  );
};

// Draw a chart at its container's real pixel width instead of stretching a
// fixed viewBox. Stretching scales the text with the window — axis labels got
// huge on a wide screen. Measured, the text stays at its set size and the
// plot simply gets wider. A callback ref, so it keeps working when the
// element it measures is swapped out.
export const useWidth = (initial = 1000, min = 360) => {
  const [w, setW] = useState(initial);
  const ro = useRef(null);
  const ref = useCallback(el => {
    if (ro.current) { ro.current.disconnect(); ro.current = null; }
    if (!el || typeof ResizeObserver === "undefined") return;
    const o = new ResizeObserver(([e]) => {
      const v = Math.max(min, Math.round(e.contentRect.width));
      setW(p => (p === v ? p : v));
    });
    o.observe(el);
    ro.current = o;
  }, [min]);
  return [ref, w];
};
